/**
 * Shared Floating UI utilities for tooltips, popovers, and highlight popovers
 * Provides consistent positioning logic and middleware configurations
 */
import type { Placement, Middleware } from '@floating-ui/react';
import { offset, flip, shift, arrow } from '@floating-ui/react';

/**
 * Extracts the base placement from a placement string
 * @param placement - The full placement string (e.g., "bottom-start")
 * @returns The base placement (e.g., "bottom")
 */
export function getBasePlacement(placement?: Placement): string {
  return (placement || 'bottom').split('-')[0];
}

/**
 * Calculates arrow positioning styles based on placement
 * Used for positioning the arrow element in popovers and tooltips
 * @param placement - The placement of the floating element
 * @param arrowHeight - The height of the arrow element (default: 8px)
 * @returns CSS properties for positioning the arrow
 */
export function getArrowPosition(
  placement?: Placement,
  arrowHeight = 8,
): React.CSSProperties {
  const basePlacement = getBasePlacement(placement);
  if (basePlacement === 'top') {
    return { bottom: `-${arrowHeight / 2}px` };
  } else if (basePlacement === 'bottom') {
    return { top: `-${arrowHeight / 2}px` };
  } else if (basePlacement === 'left') {
    return { right: `-${arrowHeight / 2}px` };
  } else if (basePlacement === 'right') {
    return { left: `-${arrowHeight / 2}px` };
  }
  return {};
}

/**
 * Default middleware configuration for standard floating elements
 * Includes offset, flip, and shift for optimal positioning
 */
export const defaultMiddleware: Middleware[] = [
  offset(8),
  flip({ padding: 8 }),
  shift({ padding: 8 }),
];

/**
 * Creates middleware configuration for popovers with arrow support
 * @param arrowRef - Ref to the arrow element
 * @param offsetValue - Distance from reference element, can be number or object with mainAxis/crossAxis (default: 8px)
 * @param showArrow - Whether to include arrow middleware (default: false)
 * @returns Middleware array with offset, flip, shift, and optionally arrow
 */
export function createPopoverMiddleware(
  arrowRef: React.RefObject<HTMLElement>,
  offsetValue: number | { mainAxis?: number; crossAxis?: number } = 8,
  showArrow = false,
): Middleware[] {
  const middleware: Middleware[] = [offset(offsetValue), flip(), shift()];

  if (showArrow) {
    middleware.push(
      arrow({
        element: arrowRef,
        padding: 4,
      }),
    );
  }

  return middleware;
}

/**
 * Creates middleware configuration for tooltips with arrow support
 * @param arrowRef - Ref to the arrow element
 * @param offsetValue - Distance from reference element (default: 8px)
 * @returns Middleware array with offset, flip, shift, and arrow
 */
export function createTooltipMiddleware(
  arrowRef: React.RefObject<HTMLElement>,
  offsetValue = 16,
): Middleware[] {
  return [
    offset(offsetValue),
    flip(),
    shift(),
    arrow({
      element: arrowRef,
      padding: 4,
    }),
  ];
}

/**
 * Creates middleware configuration for highlight popovers with custom offset
 * @param arrowRef - Ref to the arrow element
 * @param isCreateOrEdit - Whether this is a create or edit-toolbar popover (uses larger offset)
 * @returns Middleware array with offset, flip, shift, and arrow
 */
export function createHighlightPopoverMiddleware(
  arrowRef: React.RefObject<HTMLElement>,
  isCreateOrEdit: boolean,
): Middleware[] {
  return [
    offset(isCreateOrEdit ? 12 : 8),
    flip(),
    shift(),
    arrow({
      element: arrowRef,
      padding: 4,
    }),
  ];
}
